import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor,
  useSensor, useSensors, closestCorners,
} from "@dnd-kit/core";
import { useDroppable } from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";
import {
  Search, Sparkles, User, Calendar, CheckSquare, FileText, GripVertical, Copy, X, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { api, fmtDate, fmtEuro, formatApiErrorDetail } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";

/* ------------------------------------------------------------------ helpers */
const stageLabel = (stage, lang) => (lang === "fr" ? stage.label_fr : stage.label_en);

/* ------------------------------------------------------------------ page */
export default function Pipeline() {
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [data, setData] = useState(null); // { stages, columns, counters, negotiators }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [negotiatorFilter, setNegotiatorFilter] = useState("all");
  const [activeCard, setActiveCard] = useState(null);
  const [suggestFor, setSuggestFor] = useState(null); // { card, loading, text, error }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (stageFilter !== "all") params.stage = stageFilter;
      if (negotiatorFilter !== "all") params.negotiator = negotiatorFilter;
      const { data: res } = await api.get("/pipeline", { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter, negotiatorFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useSyncedReload(fetchData);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const findCard = useCallback((cardId) => {
    if (!data) return null;
    for (const stageKey of Object.keys(data.columns)) {
      const found = data.columns[stageKey].find((c) => c.id === cardId);
      if (found) return { card: found, stageKey };
    }
    return null;
  }, [data]);

  const handleDragStart = (event) => {
    const found = findCard(event.active.id);
    if (found) setActiveCard(found.card);
  };

  const handleDragEnd = async (event) => {
    setActiveCard(null);
    const { active, over } = event;
    if (!over) return;
    const cardInfo = findCard(active.id);
    if (!cardInfo) return;
    const targetStage = over.id.toString().startsWith("stage:")
      ? over.id.toString().slice(6)
      : findCard(over.id.toString())?.stageKey;
    if (!targetStage || targetStage === cardInfo.stageKey) return;

    // Prompt for a lost reason so it's captured for reporting
    let lostReason = null;
    if (targetStage === "closed_lost") {
      lostReason = window.prompt(t("lostReasonPrompt"), "");
      if (lostReason === null) return; // user cancelled
    }
    let completionDate = null;
    if (targetStage === "sold") {
      completionDate = window.prompt(t("completionDatePrompt"), new Date().toISOString().slice(0, 10));
      if (completionDate === null) return;
    }

    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, columns: { ...prev.columns } };
      next.columns[cardInfo.stageKey] = next.columns[cardInfo.stageKey].filter((c) => c.id !== active.id);
      next.columns[targetStage] = [
        { ...cardInfo.card, pipeline_stage: targetStage, days_in_stage: 0 },
        ...next.columns[targetStage],
      ];
      return next;
    });

    try {
      await api.put(`/pipeline/${active.id}/stage`, {
        stage: targetStage,
        ...(lostReason ? { lost_reason: lostReason } : {}),
        ...(completionDate ? { completion_date: completionDate } : {}),
      });
      toast.success(t("stageMoved"));
      fetchData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      fetchData();
    }
  };

  const openSuggest = async (card) => {
    setSuggestFor({ card, loading: true, text: "", error: "" });
    try {
      const { data: res } = await api.post(`/pipeline/${card.id}/ai-suggest`);
      setSuggestFor({ card, loading: false, text: res.suggestion, error: "", stageLabel: res.stage_label });
    } catch (err) {
      setSuggestFor({ card, loading: false, text: "", error: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    }
  };

  const counterTiles = useMemo(() => {
    if (!data?.counters) return [];
    const c = data.counters;
    return [
      { key: "new_vendor_leads", label: t("kpiNewLeads"), value: c.new_vendor_leads, stage: "new_vendor_lead" },
      { key: "contact_made", label: t("kpiContactMade"), value: c.contact_made, stage: "contact_made" },
      { key: "valuations_booked", label: t("kpiValuationsBooked"), value: c.valuations_booked, stage: "valuation_booked" },
      { key: "valuations_completed", label: t("kpiValuationsCompleted"), value: c.valuations_completed, stage: "valuation_completed" },
      { key: "awaiting_decision", label: t("kpiAwaitingDecision"), value: c.awaiting_decision, stage: "awaiting_decision" },
      { key: "mandates_signed", label: t("kpiMandatesSigned"), value: c.mandates_signed, stage: "mandate_signed" },
      { key: "closed_won", label: t("kpiClosedWon"), value: c.closed_won, stage: "closed_won" },
      { key: "live_listings", label: t("kpiLiveListings"), value: c.live_listings, stage: "property_live" },
      { key: "viewings_today", label: t("kpiViewingsToday"), value: c.viewings_today },
      { key: "active_offers", label: t("kpiActiveOffers"), value: c.active_offers },
      { key: "under_offer", label: t("kpiUnderOffer"), value: c.under_offer, stage: "under_offer" },
      { key: "sold_this_month", label: t("kpiSoldThisMonth"), value: c.sold_this_month, stage: "sold" },
      { key: "closed_lost", label: t("kpiClosedLost"), value: c.closed_lost, stage: "closed_lost" },
    ];
  }, [data, t]);

  return (
    <div className="space-y-8" data-testid="pipeline-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium text-stone-900">{t("pipelineTitle")}</h1>
        <p className="text-sm text-stone-500 mt-2">{t("pipelineSubtitle")}</p>
      </div>

      {/* Counter tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="pipeline-counters">
        {counterTiles.map((tile) => (
          <button
            key={tile.key}
            data-testid={`counter-${tile.key}`}
            onClick={() => tile.stage && setStageFilter(tile.stage)}
            className={`text-left bg-white border rounded-xl p-4 transition-all hover:border-stone-400 hover:shadow-sm ${
              tile.stage && stageFilter === tile.stage ? "border-stone-900 ring-1 ring-stone-900" : "border-stone-200"
            }`}
          >
            <div className="text-2xl font-heading font-medium text-stone-900">{tile.value ?? 0}</div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mt-1 leading-tight">{tile.label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            data-testid="pipeline-search"
            placeholder={t("pipelineSearchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="pipeline-stage-filter">
            <SelectValue placeholder={t("allStages")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allStages")}</SelectItem>
            {(data?.stages || []).map((s) => (
              <SelectItem key={s.key} value={s.key}>{stageLabel(s, lang)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data?.negotiators?.length > 0 && (
          <Select value={negotiatorFilter} onValueChange={setNegotiatorFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="pipeline-negotiator-filter">
              <SelectValue placeholder={t("allNegotiators")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allNegotiators")}</SelectItem>
              {data.negotiators.map((n) => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {(stageFilter !== "all" || negotiatorFilter !== "all" || search) && (
          <Button
            variant="ghost"
            data-testid="pipeline-clear-filters"
            onClick={() => { setStageFilter("all"); setNegotiatorFilter("all"); setSearch(""); }}
            className="text-stone-500"
          >
            {t("clearFilters")}
          </Button>
        )}
      </div>

      {/* Kanban board */}
      {loading && !data ? (
        <div className="text-center py-20 text-stone-400 font-heading text-lg">{t("loading")}...</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-6 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 snap-x snap-mandatory">
            {(data?.stages || []).map((stage) => (
              <StageColumn
                key={stage.key}
                stage={stage}
                lang={lang}
                cards={data.columns[stage.key] || []}
                onCardClick={(card) => navigate(`/properties?open=${card.id}`)}
                onSuggest={openSuggest}
                t={t}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard ? <PropertyCard card={activeCard} isDragging t={t} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* AI Suggest dialog */}
      <Dialog open={!!suggestFor} onOpenChange={(open) => !open && setSuggestFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="ai-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles size={18} className="text-[#9F8E7E]" />
              {t("aiSuggestTitle")}
            </DialogTitle>
            <DialogDescription>
              {suggestFor?.card && (
                <>{suggestFor.card.ref_code || suggestFor.card.name} · {suggestFor.stageLabel || ""}</>
              )}
            </DialogDescription>
          </DialogHeader>
          {suggestFor?.loading && <div className="py-10 text-center text-stone-400">{t("generating")}</div>}
          {suggestFor?.error && <div className="text-sm text-red-600" data-testid="ai-suggest-error">{suggestFor.error}</div>}
          {suggestFor?.text && (
            <>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 max-h-[50vh] overflow-y-auto whitespace-pre-wrap text-sm text-stone-800" data-testid="ai-suggest-text">
                {suggestFor.text}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  data-testid="ai-suggest-copy"
                  onClick={() => { navigator.clipboard.writeText(suggestFor.text); toast.success(t("copied")); }}
                >
                  <Copy size={14} className="mr-1.5" /> {t("copy")}
                </Button>
                <Button
                  data-testid="ai-suggest-close"
                  onClick={() => setSuggestFor(null)}
                  className="bg-stone-900 hover:bg-stone-800 text-white"
                >
                  {t("close")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ Column */
function StageColumn({ stage, lang, cards, onCardClick, onSuggest, t }) {
  const { setNodeRef, isOver } = useDroppable({ id: `stage:${stage.key}` });
  return (
    <div
      ref={setNodeRef}
      data-testid={`stage-column-${stage.key}`}
      className={`flex-shrink-0 w-72 sm:w-80 snap-start bg-stone-50/60 border rounded-2xl p-3 transition-colors ${
        isOver ? "border-stone-900 bg-stone-100" : "border-stone-200"
      }`}
    >
      <div className="flex items-center justify-between px-1 pb-3">
        <div>
          <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wider">{stageLabel(stage, lang)}</h2>
          <p className="text-[11px] text-stone-400 mt-0.5 leading-tight">{stage.desc_en}</p>
        </div>
        <span className="text-xs font-semibold text-stone-500 bg-white border border-stone-200 rounded-full px-2 py-0.5">{cards.length}</span>
      </div>
      <div className="space-y-2 min-h-[80px]">
        {cards.length === 0 && (
          <div className="text-xs text-stone-400 italic text-center py-6">{t("stageEmpty")}</div>
        )}
        {cards.map((card) => (
          <DraggableCard key={card.id} card={card} onClick={onCardClick} onSuggest={onSuggest} t={t} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Card */
function DraggableCard({ card, onClick, onSuggest, t }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: card.id });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.3 : 1 }
    : undefined;
  return (
    <div ref={setNodeRef} style={style} data-testid={`pipeline-card-${card.id}`}>
      <PropertyCard
        card={card}
        onClick={onClick}
        onSuggest={onSuggest}
        dragHandle={{ ...attributes, ...listeners }}
        t={t}
      />
    </div>
  );
}

function PropertyCard({ card, onClick, onSuggest, dragHandle, isDragging, t }) {
  return (
    <div
      className={`bg-white border border-stone-200 rounded-xl p-3 shadow-sm transition-all ${
        isDragging ? "shadow-lg rotate-1" : "hover:border-stone-400"
      }`}
    >
      <div className="flex items-start gap-2">
        <button
          {...(dragHandle || {})}
          data-testid={`drag-handle-${card.id}`}
          aria-label="Drag"
          className="mt-1 p-0.5 text-stone-300 hover:text-stone-600 cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical size={14} />
        </button>
        <button
          type="button"
          onClick={() => onClick && onClick(card)}
          data-testid={`card-body-${card.id}`}
          className="flex-1 min-w-0 text-left"
        >
          {card.photo && (
            <img src={card.photo} alt="" className="w-full h-24 object-cover rounded-lg mb-2 bg-stone-100" />
          )}
          <div className="flex items-center gap-1.5 mb-1">
            {card.ref_code && (
              <span className="text-[10px] font-mono text-stone-500 bg-stone-100 rounded px-1.5 py-0.5">{card.ref_code}</span>
            )}
            <p className="text-sm font-medium text-stone-900 truncate">{card.name || t("noAddress")}</p>
          </div>
          {card.location_public && (
            <p className="text-xs text-stone-500 truncate mb-1.5">{card.location_public}</p>
          )}
          <p className="text-sm font-semibold text-stone-800 mb-2">{fmtEuro(card.price_total || card.price_net)}</p>
          <div className="flex flex-wrap gap-1.5 text-[11px] text-stone-500">
            {card.seller_name && (
              <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5">
                <User size={10} />{card.seller_name}
              </span>
            )}
            <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5" data-testid={`card-days-${card.id}`}>
              {card.days_in_stage}d
            </span>
            {card.viewings_count > 0 && (
              <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5" data-testid={`card-viewings-${card.id}`}>
                <Eye size={10} />{card.viewings_count}
              </span>
            )}
            {card.active_offers_count > 0 && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5" data-testid={`card-offers-${card.id}`}>
                <FileText size={10} />{card.active_offers_count} {t("offersShort")}
              </span>
            )}
            {card.next_activity && (
              <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5" data-testid={`card-next-${card.id}`}>
                <Calendar size={10} />{fmtDate(card.next_activity)}
              </span>
            )}
            {card.open_tasks > 0 && (
              <span className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 rounded px-1.5 py-0.5" data-testid={`card-tasks-${card.id}`}>
                <CheckSquare size={10} />{card.open_tasks}
              </span>
            )}
            {card.assigned_negotiator && (
              <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5">
                {card.assigned_negotiator}
              </span>
            )}
          </div>
        </button>
      </div>
      {onSuggest && (
        <button
          type="button"
          data-testid={`ai-suggest-btn-${card.id}`}
          onClick={(e) => { e.stopPropagation(); onSuggest(card); }}
          className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs text-stone-600 hover:text-stone-900 border border-dashed border-stone-300 hover:border-stone-500 rounded-lg py-1.5 transition-colors"
        >
          <Sparkles size={12} className="text-[#9F8E7E]" /> {t("aiSuggestNext")}
        </button>
      )}
    </div>
  );
}
